import { nameAndLifetimeOf } from './Utils';
import {
    Family,
    Individual,
    IndividualCallback,
    LifeEvent,
    Sex,
} from './Data';
import styles from './IndividualDetail.module.css';

function formatEvent(event?: LifeEvent) {
    if (event === undefined || (!event.date && !event.location)) {
        return '';
    }
    let s = '';
    if (event.date) {
        s += event.date;
    }
    if (event.location) {
        if (s) {
            s += ', ';
        }
        s += event.location;
    }
    return s;
}

function formatChildren(family: Family, callback: IndividualCallback) {
    if (family.children.length === 0) {
        return null;
    }
    let first = true;
    const f = (child: Individual) => {
        const leftColumn = first ? (
            <span className={styles.fieldTitle}>Children:</span>
        ) : null;
        first = false;
        return (
            <tr key={family.id + '-' + child.id}>
                <td>{leftColumn}</td>
                <td>
                    <button
                        className={styles.linkButton}
                        onClick={() => callback(child.id)}
                    >
                        {nameAndLifetimeOf(child)}
                    </button>
                </td>
            </tr>
        );
    };
    return family.children.map(f);
}

function spouseOf(
    individual: Individual,
    family: Family,
): Individual | undefined {
    for (const partner of family.partners) {
        if (partner.id !== individual.id) {
            return partner;
        }
    }
    return undefined;
}

function formatFamilies(
    individual: Individual,
    families: Family[],
    callback: IndividualCallback,
) {
    const f = (family: Family) => {
        const spouse = spouseOf(individual, family);
        const spouseBox =
            spouse !== undefined ? (
                <button
                    className={styles.linkButton}
                    onClick={() => callback(spouse.id)}
                >
                    {nameAndLifetimeOf(spouse)}
                </button>
            ) : null;
        return (
            <table key={'family' + family.id} className={styles.familyTable}>
                <tbody>
                    <tr>
                        <td>
                            <span className={styles.fieldTitle}>Partner:</span>
                        </td>
                        <td>{spouseBox}</td>
                    </tr>
                    <tr>
                        <td>
                            <span className={styles.fieldTitle}>Married:</span>
                        </td>
                        <td>{formatEvent(family.married)}</td>
                    </tr>
                    {formatChildren(family, callback)}
                </tbody>
            </table>
        );
    };
    return families.map(f);
}

function formatParents(parents: Individual[], callback: IndividualCallback) {
    let first = true;
    const f = (p: Individual) => {
        const leftColumn = first ? (
            <span className={styles.fieldTitle}>Parents:</span>
        ) : null;
        first = false;
        return (
            <tr key={'parent-' + p.id}>
                <td>{leftColumn}</td>
                <td>
                    <button
                        className={styles.linkButton}
                        onClick={() => callback(p.id)}
                    >
                        {nameAndLifetimeOf(p)}
                    </button>
                </td>
            </tr>
        );
    };
    return parents.map(f);
}

interface IndividualDetailProps {
    individual: Individual;
    showDetails: IndividualCallback;
    showDescendants: IndividualCallback;
    showAncestors: IndividualCallback;
}

export function IndividualDetail({
    individual,
    showDetails,
    showDescendants,
    showAncestors,
}: IndividualDetailProps) {
    const families = individual.partnerIn || [];
    const parents = individual.childOf ? individual.childOf.partners : [];

    const birth = formatEvent(individual.birth);
    const death = formatEvent(individual.death);
    const buried = formatEvent(individual.buried);
    const baptism = formatEvent(individual.baptism);

    const sex =
        individual.sex === Sex.Male
            ? 'Male'
            : individual.sex === Sex.Female
            ? 'Female'
            : '?';

    const fields = [
        ['Last name', individual.lastName],
        ['First names', individual.firstNames],
        ['Sex', sex],
        ['Birth', birth],
        ['Death', death],
        ['Buried', buried],
        ['Baptism', baptism],
        ['Occupation', individual.occupation],
    ];

    const fieldsRows = fields.map((x) => {
        return (
            <tr key={x[0]}>
                <td>
                    <span className={styles.fieldTitle}>{x[0]}:</span>
                </td>
                <td>{x[1]}</td>
            </tr>
        );
    });

    const parentsRows = formatParents(parents, showDetails);

    const familiesRows = formatFamilies(
        individual,
        families,
        showDetails,
    );

    return (
        <div className={styles.container}>
            <div className={styles.name}>
                {individual.firstNames + ' ' + individual.lastName}
            </div>
            <table className={styles.detailTable}>
                <tbody>
                    {fieldsRows}
                    {parentsRows}
                </tbody>
            </table>

            <div className={styles.familiesSection}>
                <span className={styles.familiesTitle}>Families:</span>
                {familiesRows}
            </div>

            {individual.note && (
                <div className={styles.notes}>
                    <span className={styles.notesTitle}>Notes:</span>
                    <div>{individual.note}</div>
                </div>
            )}

            <div className={styles.actions}>
                <button
                    className={styles.actionButton}
                    onClick={() => showDescendants(individual.id)}
                >
                    View Descendants Tree
                </button>
                <button
                    className={styles.actionButton}
                    onClick={() => showAncestors(individual.id)}
                >
                    View Ancestors Tree
                </button>
            </div>
        </div>
    );
}
